import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Avatar,
  CircularProgress,
  IconButton,
  Grid,
  Chip,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BadgeIcon from "@mui/icons-material/Badge";

import { useNotification } from "../../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../../Hooks/useApiErrorHandler";
import useTokenRefresh from "../../../../Hooks/useTokenRefresh";
import httpclient from "../../../../Utils";

const DriverDetailsModal = ({ open, onClose, driverId }) => {
  const [loading, setLoading] = useState(false);
  const [driverDetails, setDriverDetails] = useState(null);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();

  useEffect(() => {
    const fetchDriverDetails = async () => {
      if (open && driverId) {
        setLoading(true);

        const API_URL = `delivery/drivers/details/${driverId}`;

        try {
          const res = await httpclient.get(API_URL);
          const { data } = res;

          if (data.status === 200) {
            setDriverDetails(data.data);
          } else {
            showNotification(
              "error",
              data.message || "Failed to fetch driver details"
            );
          }
        } catch (error) {
          if (error.response?.status === 401) {
            refresh();
          } else {
            handleApiError(error);
          }
        } finally {
          setLoading(false);
        }
      }
    };

    fetchDriverDetails();
  }, [open, driverId]);

  const handleClose = () => {
    setDriverDetails(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "white",
          p: 3,
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display="flex" alignItems="center" gap={2}></Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: "#48505E",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, backgroundColor: "#f8f9fa" }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress size={50} />
          </Box>
        ) : driverDetails ? (
          <Box>
            {/* Profile Card */}
            <Card
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                background: "white",
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  textAlign="center"
                >
                  <Avatar
                    src={driverDetails.profile_photo}
                    alt={`${driverDetails.first_name} ${driverDetails.last_name}`}
                    sx={{
                      width: 140,
                      height: 140,
                      mb: 2,
                      border: "4px solid #f0f0f0",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    {driverDetails.first_name} {driverDetails.last_name}
                  </Typography>
                  <Chip
                    label={driverDetails.status === 1 ? "Active" : "Inactive"}
                    color={driverDetails.status === 1 ? "success" : "default"}
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      px: 2,
                      height: 32,
                    }}
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Information Cards */}

            <Grid container spacing={2}>
              {/* Email Card */}
              <Grid item xs={12}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    transition: "all 0.3s",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          backgroundColor: "#E3F2FD",
                          borderRadius: "12px",
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <EmailIcon sx={{ color: "#1976d2", fontSize: 28 }} />
                      </Box>
                      <Box flex={1}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          Email Address
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={500}
                          sx={{ mt: 0.5 }}
                        >
                          {driverDetails.email || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Phone Card */}
              <Grid item xs={12} sm={6}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    height: "100%",
                    transition: "all 0.3s",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          backgroundColor: "#F3E5F5",
                          borderRadius: "12px",
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PhoneIcon sx={{ color: "#9c27b0", fontSize: 28 }} />
                      </Box>
                      <Box flex={1}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          Phone
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={500}
                          sx={{ mt: 0.5 }}
                        >
                          {driverDetails.phone || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Employee Code Card */}
              <Grid item xs={12} sm={6}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    transition: "all 0.3s",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          backgroundColor: "#FFF3E0",
                          borderRadius: "12px",
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <BadgeIcon sx={{ color: "#f57c00", fontSize: 28 }} />
                      </Box>
                      <Box flex={1}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          Employee Code
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={500}
                          sx={{ mt: 0.5 }}
                        >
                          {driverDetails.employee_code || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <Typography variant="h6" color="text.secondary">
              No driver details available
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DriverDetailsModal;
